import { startOfToday } from "date-fns";

import {
    BugGroupEntity,
    ProjectEntity,
    RemarkCategoryHierarchyEntity,
    RemarkEntity,
    VisitEntity,
} from "@archipad-js/archipad-model";
import { LoggerManager } from "@archipad-js/core/logger";
import { Expression } from "@archipad-js/core/query";
import { query } from "@archipad-js/core/orm";
import { formatTextList, isNotNullish } from "@archipad-js/core/utils";

import { VisitTypeService } from "./visittype.service";
import { VisitService } from "./visit.service";

/**
 * Dependency-inversion interface providing permission data required by {@link RemarkFilterService}.
 *
 * Implemented by the legacy `WorkflowUserManager`.
 */
export interface RemarkFilterUserProvider {
    bugGroupsAllowingReadNotNull(project: ProjectEntity): ReadonlyArray<BugGroupEntity>;
}

export class RemarkFilterService {
    protected readonly logger = LoggerManager.getLogger("RemarkFilterService");

    constructor(
        private readonly visitService: VisitService,
        private readonly visitTypeService: VisitTypeService,

        private readonly workflowUserManager: RemarkFilterUserProvider,
    ) {}

    public listRemarks(options: {
        filterContext: RemarkFilterContext;
        filterQuery: RemarkFilterQuery;
        orderBy?: string;
    }): readonly RemarkEntity[] {
        const defaultOrderBy = "openDate asc, time asc, id asc";
        const { orderBy = defaultOrderBy } = options;
        const entityContext = options.filterContext.project.getContext();

        const q = query(entityContext, "Remark")
            .predicate(this.makeRemarkFilterExpression(options.filterContext, options.filterQuery))
            .orderBy(formatTextList([orderBy, "id asc"], ", "));

        const resultSet = q.execute();
        const remarks = resultSet.sortedEntities();
        return remarks;
    }

    public makeRemarkFilterExpression(context: RemarkFilterContext, filters: RemarkFilterQuery): Expression {
        const expression = Expression.and(
            Expression.parse("project == {project}", { project: context.project }),
            this.makeStatesFilterExpression(context, filters),
            this.makeCategoriesFilterExpression(context, filters),
            this.makeParentsFilterExpression(context, filters),
            this.makeVisitsFilterExpression(context, filters),
            this.makeStatusesFilterExpression(context, filters),
            this.makeConcernsFilterExpression(context, filters),
        );
        return expression;
    }

    public listStates(context: RemarkFilterContext, filters: RemarkFilterQuery): RemarkComputedState[] {
        const { visit: visitContext } = context;
        const visitStates: RemarkComputedState[] = visitContext
            ? [RemarkComputedState.CloseInVisit]
            : [RemarkComputedState.CloseToday];

        return [RemarkComputedState.Active, ...visitStates, RemarkComputedState.Closed];
    }

    public listVisits(context: RemarkFilterContext, filters: RemarkFilterQuery): readonly VisitEntity[] {
        const { project } = context;
        const bugGroupsAllowingRead = this.workflowUserManager.bugGroupsAllowingReadNotNull(project);
        const visitTypes = this.visitTypeService.listVisitTypes({
            where: {
                project,
                bugGroups: bugGroupsAllowingRead,
            },
        });
        const allowVisits = this.visitService.listVisits({
            where: {
                project,
                visitTypes,
            },
            orderBy: "date desc, object desc, id desc",
        });
        const remarksVisits = Array.from(
            new Set(project.remarks.flatMap((r) => [r.visit, r.closeVisit]).filter(isNotNullish)),
        );

        const visibleVisits = allowVisits.filter((v) => remarksVisits.includes(v));

        return visibleVisits;
    }

    public listStatuses(context: RemarkFilterContext, filters: RemarkFilterQuery): Array<RemarkEntity["status"]> {
        const { project } = context;
        const statusesSet = new Set<RemarkEntity["status"]>();
        project.remarks.forEach((remark) => {
            if (remark.status) {
                statusesSet.add(remark.status);
            }
        });
        return Array.from(statusesSet);
    }

    public listConcerns(context: RemarkFilterContext, filters: RemarkFilterQuery): Array<RemarkEntity["contact"]> {
        const { project } = context;
        const concernsSet = new Set<RemarkEntity["contact"]>();
        project.remarks.forEach((remark) => {
            if (remark.contact) {
                concernsSet.add(remark.contact);
            }
        });
        return Array.from(concernsSet);
    }

    private makeStatesFilterExpression(context: RemarkFilterContext, filters: RemarkFilterQuery): Expression {
        if (filters.states && filters.states.length > 0) {
            const stateExpressions = filters.states.map((state) =>
                this.makeStateFilterExpression(state, context.visit ?? null),
            );
            return Expression.or(...stateExpressions);
        }
        return Expression.true;
    }

    private makeStateFilterExpression(state: RemarkComputedState, visitContext: VisitEntity | null): Expression {
        const visit = visitContext;
        if (state === undefined || state === null) {
            return Expression.true;
        }
        switch (state) {
            case RemarkComputedState.Active: {
                return Expression.or(
                    Expression.parse("closeDate == null"),
                    Expression.parse("SUB_ANY(childrenRemarks, closeDate == null)"),
                );
            }
            case RemarkComputedState.Closed: {
                return Expression.or(
                    Expression.parse("closeDate != null"),
                    Expression.parse("SUB_ANY(childrenRemarks, closeDate != null)"),
                );
            }
            case RemarkComputedState.CloseToday: {
                const todayStart = startOfToday();
                return Expression.or(
                    Expression.parse("startOfToday(closeDate) >= {todayStart} ", { todayStart }),
                    Expression.parse("SUB_ANY(childrenRemarks, startOfToday(closeDate) >= {todayStart})", {
                        todayStart,
                    }),
                );
            }
            case RemarkComputedState.CloseInVisit: {
                return Expression.or(
                    Expression.parse("closeVisit in {visits}", { visits: [visit] }),
                    Expression.parse("SUB_ANY(childrenRemarks, closeVisit in {visits})", { visits: [visit] }),
                );
            }
        }
    }

    private makeCategoriesFilterExpression(context: RemarkFilterContext, filters: RemarkFilterQuery): Expression {
        if (filters.categoriesHierarchy) {
            return Expression.parse("categoryHierarchy in {categoriesHierarchy}", {
                categoriesHierarchy: filters.categoriesHierarchy,
            });
        } else if (filters.categoriesHierarchy === null) {
            return Expression.parse("categoryHierarchy == null AND parentRemark == null");
        }
        return Expression.true;
    }

    private makeParentsFilterExpression(context: RemarkFilterContext, filters: RemarkFilterQuery): Expression {
        if (filters.parents) {
            return Expression.parse("parentRemark in {parents}", { parents: filters.parents });
        } else if (filters.parents === null) {
            return Expression.parse("parentRemark == null");
        }
        return Expression.true;
    }

    private makeVisitsFilterExpression(context: RemarkFilterContext, filters: RemarkFilterQuery): Expression {
        if (filters.visits && filters.visits.length > 0) {
            const visits = filters.visits.filter(isNotNullish);
            const includesNoVisit = filters.visits.includes(null);

            const expressions: Expression[] = [];

            if (visits.length > 0) {
                expressions.push(
                    Expression.parse("visit in {visits}", { visits }),
                    Expression.parse("closeVisit in {visits}", { visits }),
                    Expression.parse("SUB_ANY(childrenRemarks, visit in {visits})", { visits }),
                    Expression.parse("SUB_ANY(childrenRemarks, closeVisit in {visits})", { visits }),
                );
            }

            // "Sans visite": remarks created outside of any visit (visit == null).
            // Cannot rely on `visit in {visits}` since the IN operator does not match null values.
            if (includesNoVisit) {
                expressions.push(
                    Expression.parse("visit == null"),
                    Expression.parse("SUB_ANY(childrenRemarks, visit == null)"),
                );
            }

            return Expression.or(...expressions);
        } else if (filters.visits === null) {
            return Expression.parse("visit == null");
        }
        return Expression.true;
    }

    private makeStatusesFilterExpression(context: RemarkFilterContext, filters: RemarkFilterQuery): Expression {
        if (filters.statuses && filters.statuses.length > 0) {
            if (filters.statuses.includes(null)) {
                Expression.or(
                    Expression.parse("status in {statuses} OR status == null OR status == ''", {
                        statuses: filters.statuses.filter((s) => s !== null),
                    }),
                    Expression.parse(
                        "SUB_ANY(childrenRemarks, status in {statuses} OR status == null OR status == '')",
                        { statuses: filters.statuses.filter((s) => s !== null) },
                    ),
                );
            } else {
                return Expression.or(
                    Expression.parse("status in {statuses}", { statuses: filters.statuses }),
                    Expression.parse("SUB_ANY(childrenRemarks, status in {statuses})", { statuses: filters.statuses }),
                );
            }
        } else if (filters.statuses === null) {
            return Expression.or(
                Expression.parse("status == null"),
                Expression.parse("SUB_ANY(childrenRemarks, status == null)"),
            );
        }
        return Expression.true;
    }

    private makeConcernsFilterExpression(context: RemarkFilterContext, filters: RemarkFilterQuery): Expression {
        if (filters.concerns && filters.concerns.length > 0) {
            // if (filters.concerns.includes(null)) {
            //     return Expression.parse("contact in {concerns} OR contact == null", { concerns: filters.concerns.filter(c => c !== null) });
            // } else {
            return Expression.or(
                Expression.parse("contact in {concerns}", { concerns: filters.concerns }),
                Expression.parse("SUB_ANY(childrenRemarks, contact in {concerns})", { concerns: filters.concerns }),
            );
            // }
        }
        return Expression.true;
    }
}

export type RemarkFilterContext = {
    project: ProjectEntity;
    visit?: VisitEntity | null;
};

export type RemarkFilterQuery = {
    visits?: Array<VisitEntity | null> | null;
    states?: Array<RemarkComputedState> | null;
    categoriesHierarchy?: RemarkCategoryHierarchyEntity[] | null;
    parents?: RemarkEntity[] | null;
    statuses?: Array<RemarkEntity["status"] | null>;
    concerns?: Array<RemarkEntity["contact"] | null>;
};
export enum RemarkComputedState {
    Active,
    Closed,
    CloseToday,
    CloseInVisit,
}
